import { FC, useEffect, useMemo, useRef, useState } from 'react';
import cs from 'classnames';
import { useMethods } from '@lhb/hook';
import { floorKeep, isNotEmpty, replaceEmpty } from '@lhb/func';
import { getPlaceMallDetail } from '@/common/api/youth';
import { getQiniuOriginalUrl } from '@/common/utils/image';
import styles from './index.module.less';
import { Image } from 'antd';
import IconFont from 'src/common/components/Base/IconFont/index';
import V2DetailGroup from 'src/common/components/Feedback/V2DetailItem/Group';
import V2DetailItem from 'src/common/components/Feedback/V2DetailItem/index';
import StatisticsItem from './StatisticsItem';
import DetailModal from './DetailModal';
import Statistics from './Statistics';
import { useSelector } from 'react-redux';

/**
 * 商场模块-商场信息
 * @description
 * @demo
 */
const Component:FC<{
  /** 商圈id */
  modelClusterId: number,
  /** 商圈详情 */
  detail: any;
  shareParams?: any;
  setShowMallInfo?: any;
  overViewData?: any;
}> = ({ modelClusterId, detail, overViewData, shareParams = {}, setShowMallInfo = () => {} }) => {
  const defaultConfig = useSelector((state: any) => state.youth.defaultConfig);
  const shareDefaultConfig = useSelector((state: any) => state.share.defaultConfig);
  const modelClusterInfo = useMemo(() => {
    const show = shareParams.id ? shareDefaultConfig?.modelClusterInfo : defaultConfig?.modelClusterInfo;
    setShowMallInfo(show);
    return show;
  }, [defaultConfig, shareDefaultConfig, shareParams]);

  /* state */
  const detailModalRef = useRef<any>(null);
  /** 商场信息 */
  const [placeData, setPlaceData] = useState<any>({});
  const [imgGroupVisisble, setImgGroupVisisble] = useState(false);
  /** 数据块可见：任意字段有值则展示 */
  const showStatistics = [placeData.annualSales, placeData.annualTrafficAvgDaily, placeData.commercialVolume, placeData.openingTime].some(item => isNotEmpty(item));
  /** 基础信息可见：任意字段有值则展示 */
  const showBasicInfo = [
    placeData.managerName,
    placeData.propertyBrand,
    placeData.projectIndustry,
    placeData.groundFloor,
    placeData.topFloor,
    // placeData.placeDescription,
  ].some(item => isNotEmpty(item));

  const pictures = (Array.isArray(placeData.pictures) ? placeData.pictures : []).map(item => getQiniuOriginalUrl(item));

  /* hooks */
  useEffect(() => {
    if (modelClusterId) {
      methods.init();
    }
  }, [modelClusterId]);

  /* methods */
  const methods = useMethods({

    init() {
      getPlaceMallDetail({ modelClusterId }).then((res) => {
        setPlaceData(res || {});
      });
    },

    /** 查看更多 */
    viewMore() {
      detailModalRef.current?.init();
    },

  });

  return (<div className={styles.mallInfo} style={{ display: modelClusterInfo ? 'flex' : 'none' }}>
    {/* 头部标题区域 */}
    <div className={styles.mallHeader}>
      {Array.isArray(pictures) && !!pictures.length && <div className={styles.mallHeaderImg}>
        <Image
          width='100%'
          height='100%'
          src={pictures?.[0] || `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/task/ic_place_noimg.png`}
          preview={false}
          className={styles.img}
          onClick={() => Array.isArray(pictures) && !!pictures.length ? setImgGroupVisisble(true) : {}}
        />
        <div style={{ display: 'none' }}>
          <Image.PreviewGroup preview={{ visible: imgGroupVisisble, onVisibleChange: vis => setImgGroupVisisble(vis) }}>
            {Array.isArray(pictures) ? pictures.map((item, index) => <Image key={index} src={item} />) : <></>}
          </Image.PreviewGroup>
        </div>
      </div>}
      <div className={styles.mallHeaderContent}>
        <div className={styles.mallTitle}>
          <span className={styles.text}>{replaceEmpty(detail.areaName)}</span>
          <div className={cs(styles.viewMore, 'pointer')} onClick={methods.viewMore}>
            <span>查看全部</span>
            <IconFont iconHref='iconarrow-right' className={styles.icon} />
          </div>
        </div>
        <div className={styles.mallDescription}>{replaceEmpty(placeData.placeDescription)}</div>
      </div>
    </div>

    {showStatistics || showBasicInfo
      ? <div className={styles.mallContent}>
        {/* 数据统计区域 */}
        {!!showStatistics && <div className={styles.statisticsSection}>
          {/* 处理单位：年销售额（万元） */}
          <StatisticsItem label='年销售额'
            value={formatNumForTenThousand(placeData.annualSales)?.num}
            unit={`${formatNumForTenThousand(placeData.annualSales)?.unit || ''}元`}
            help='销售额数据源自商场或集团官网公告；部分企业可能仅披露核心项目业绩，未涵盖全部业态或子公司数据。'
          />
          {/* 年日均客流（万人次） */}
          <StatisticsItem label='年日均客流'
            value={formatNumForTenThousand(placeData.annualTrafficAvgDaily)?.num}
            unit={`${formatNumForTenThousand(placeData.annualTrafficAvgDaily)?.unit || ''}人次`}
            tag={isNotEmpty(placeData.annualTrafficAvgDaily) && isNotEmpty(placeData.annualTrafficRank) ? `市内第${placeData.annualTrafficRank}` : ''}
          />
          <StatisticsItem label='商业面积'
            value={formatNum(placeData.commercialVolume)?.num}
            unit={`${formatNum(placeData.commercialVolume)?.unit || ''}㎡`}
          />
          <StatisticsItem label='开业时间' value={placeData.openingTime} />
        </div>}
        {!!showStatistics && !!showBasicInfo && <div className={styles.divider}></div>}
        {/* 详细信息区域 */}
        {!!showBasicInfo && <V2DetailGroup labelLength={4} direction='horizontal' className={styles.detailsSection}>
          {isNotEmpty(placeData.managerName) && <V2DetailItem label='运营商' value={placeData.managerName} className={styles.item} />}
          {isNotEmpty(placeData.propertyBrand) && <V2DetailItem label='商场品牌' value={placeData.propertyBrand} className={styles.item} />}
          {isNotEmpty(placeData.projectIndustry) && <V2DetailItem label='商场业态' value={placeData.projectIndustry} className={styles.item} />}
          {isNotEmpty(placeData.groundFloor) && <V2DetailItem label='最低楼层' value={placeData.groundFloor} className={styles.item} />}
          {isNotEmpty(placeData.topFloor) && <V2DetailItem label='最高楼层' value={placeData.topFloor} className={styles.item} />}
          {/* {isNotEmpty(placeData.placeDescription) && <V2DetailItem label='商场简介' type='textarea' rows={3} value={placeData.placeDescription} className={cs(styles.item, styles.fullWidth)} />} */}
        </V2DetailGroup>}
      </div>
      : <></>}
    {/* 统计信息 */}
    {isNotEmpty(detail?.salesInfo) && <Statistics detail={detail?.salesInfo} placeData={placeData} overViewData={overViewData} />}
    {/* 商场信息详情弹窗 */}
    <DetailModal ref={detailModalRef} modelClusterId={modelClusterId} clusterName={detail.areaName} placeData={placeData} />

  </div>);
};

/**
 * 格式化数字为金额显示（亿/万），传入数字计数单位为“万元”
 * @param {*} num 数字，传入数字计数单位为“万元”
 * @param {*} digits 保留小数位数，仅对未处理的数据生效（处理的数字会自动保留2位小数）
 * @return {*}
 * @demo
  值 formatNumForTenThousand(placeData.annualSales)?.num
  单位 formatNumForTenThousand(placeData.annualSales)?.unit
 */
export const formatNumForTenThousand = (num, digits = 0) => {
  if (typeof num !== 'number' || isNaN(num)) {
    return { num: null, unit: '' };
  }
  return formatNum(num * 10000, digits);
};

/**
 * @description 格式化数字为金额显示（亿/万）
 * @param {*} num 数字，传入数字计数单位为“个位”
 * @param {*} digits 保留小数位数，仅对未处理的数据生效（处理的数字会自动保留2位小数）
 * @return {*}
 * @demo
  值 formatNum(placeData.annualSales)?.num
  单位 formatNum(placeData.annualSales)?.unit
  处理万元，先转换为各位 formatNum(placeData.annualSales * 10000)?.num
  `${formatNum(47200000)?.unit || ''}元`
  `${formatNum(47200000)?.unit || ''}人`
 */
export const formatNum = (num, digits = 0) => {
  if (typeof num !== 'number' || isNaN(num)) {
    return { num: null, unit: '' };
  }

  const absNum = Math.abs(num);

  if (absNum >= 100000000) {
    return {
      num: floorKeep(num / 100000000, 1, 3, 2),
      unit: '亿'
    };
  } else if (absNum >= 10000) {
    return {
      num: floorKeep(num / 10000, 1, 3, 2),
      unit: '万'
    };
  } else {
    return {
      num: floorKeep(num, 1, 3, digits),
      unit: ''
    };
  }
};

export default Component;
